"use client";

import { useEffect, useRef, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  useMediaQuery,
  useTheme,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Pagination,
  FormControl,
  MenuItem,
  Select,
  Avatar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useNavigate } from "react-router-dom";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useDelete from "../../../../api/useDelete";
import { useTranslation } from "react-i18next";
import FreeMedicinePrint from "./FreeMedicinePrint";
import FreeMedicineEdit from "./FreeMedicineEdit";

const FreeMedicineList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // Pagination state variables
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPrescriptions, setTotalPrescriptions] = useState(0);
  const [openEdit, setOpenEdit] = useState(false);
  const [orderData, setOrderData] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedOrderForEdit, setSelectedOrderForEdit] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedPatient, setSelectedPatient] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);

  const { handleDelete: handleDeleteOrder, ConfirmDialog } = useDelete(
    "medicine-order-free",
    token
  );
  const {
    handleDelete: handleDeleteOrderItem,
    ConfirmDialog: ConfirmDialogItem,
  } = useDelete("medicine-order-item-free", token);

  // Fetch patients data
  const {
    data: patients,
    isLoading: patientLoading,
    isError: patientError,
  } = useFetchObjects(["patients"], `patients/`, token);

  // Fetch medicine orders with pagination
  const { data, isLoading, isError, isSuccess, refetch } = useFetchObjects(
    ["medicine-order-free"],
    `medicine-order-free/?page=${page}&page_size=${rowsPerPage}&search=${searchQuery}&type=${filterType}&start_datetime=${startDate}&end_datetime=${endDate}&patient=${selectedPatient}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [
    refetch,
    searchQuery,
    page,
    rowsPerPage,
    filterType,
    startDate,
    endDate,
    selectedPatient,
    paymentStatus,
  ]);

  useEffect(() => {
    if (data && data.count !== undefined) {
      setTotalPrescriptions(data.count);
    }
  }, [data]);

  useEffect(() => {
    if (data && data.results) {
      const total = data.results.reduce(
        (sum, order) => sum + Number.parseFloat(order.total_price || 0),
        0
      );
      setTotalAmount(total);
    }
  }, [data]);

  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef();

  const handleOpen = (item) => {
    setSelectedItem(item);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
  };

  const handlePrintClick = (order) => {
    setPrintData(order);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Medicine Order #${order.id}</title>
              <style>
                @page {
                  size: 80mm auto;
                  margin: 0;
                }
                body {
                  margin: 0;
                  padding: 0;
                  font-family: Arial, sans-serif;
                }
              </style>
            </head>
            <body>
              ${printRef.current.innerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(1);
  };

  // Format date to YYYY-MM-DD
  const formatDateForAPI = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        width="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box textAlign="center" mt={5}>
        <Typography variant="h6" color="error">
          {t("pharmacy.errorLoadingOrders")}
        </Typography>
        <Button variant="contained" color="primary" onClick={() => refetch()}>
          {t("pharmacy.retry")}
        </Button>
      </Box>
    );
  }

  if (isSuccess) {
    const prescriptions = data?.results || [];

    return (
      <Grid container spacing={3} mt={3} paddingX={isSmallScreen ? 2 : 3}>
        {/* Top Section: Title and Add New Button */}
        <Grid item xs={12}>
          <Box
            display="flex"
            flexDirection={isSmallScreen ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isSmallScreen ? "stretch" : "center"}
            mb={3}
            gap={isSmallScreen ? 2 : 0}
          >
            {/* Title */}
            <Typography
              variant="h4"
              fontWeight="bold"
              color="primary"
              sx={{ flex: isSmallScreen ? "none" : "1 1 auto" }}
            >
              {t("pharmacy.freeMedicineList")}
            </Typography>

            {/* Add New Button */}
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/pharmacy/free-medicine/add")}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                paddingX: 3,
                paddingY: isSmallScreen ? 1 : 1.5,
                alignSelf: isSmallScreen ? "flex-end" : "center",
                borderRadius: "8px",
                boxShadow: 3,
                transition: "all 0.3s ease",
                backgroundColor: theme.palette.primary,
                "&:hover": {
                  backgroundColor: theme.palette.primary,
                  boxShadow: 6,
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  boxShadow: 2,
                  transform: "translateY(0px)",
                },
                "& .MuiButton-startIcon": {
                  marginRight: 1,
                },
              }}
            >
              {t("pharmacy.addNew")}
            </Button>
          </Box>
        </Grid>

        {/* Search Bar */}
        <Grid item xs={12}>
          <TextField
            variant="outlined"
            size="small"
            placeholder={t("pharmacy.search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              "aria-label": t("pharmacy.search"),
            }}
            sx={{
              flex: isSmallScreen ? "none" : "2 1 auto",
              width: isSmallScreen ? "100%" : "auto",
            }}
          />
        </Grid>

        {/* Filter Button */}
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ mb: 2 }}
            >
              {showFilters
                ? t("pharmacy.hideFilters")
                : t("pharmacy.showFilters")}
            </Button>

            <Box
              sx={{
                p: 2,
                bgcolor: "primary.light",
                color: "primary.contrastText",
                borderRadius: 2,
                boxShadow: 3,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                {t("pharmacy.totalAmount")}: {totalAmount.toLocaleString()} ؋
              </Typography>
            </Box>
          </Box>

          {showFilters && (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <Typography variant="subtitle2" gutterBottom>
                      {t("pharmacy.filterByType")}
                    </Typography>
                    <Select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="all">{t("pharmacy.all")}</MenuItem>
                      <MenuItem value="daily">{t("pharmacy.daily")}</MenuItem>
                      <MenuItem value="weekly">{t("pharmacy.weekly")}</MenuItem>
                      <MenuItem value="monthly">
                        {t("pharmacy.monthly")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <Typography variant="subtitle2" gutterBottom>
                      {t("pharmacy.patient")}
                    </Typography>
                    {patientLoading ? (
                      <CircularProgress size={24} />
                    ) : patientError ? (
                      <Typography color="error" variant="caption">
                        {t("pharmacy.errorLoadingPatients")}
                      </Typography>
                    ) : (
                      <Select
                        value={selectedPatient}
                        onChange={(e) => setSelectedPatient(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          {t("pharmacy.allPatients")}
                        </MenuItem>
                        {patients &&
                          patients.map((patient) => (
                            <MenuItem key={patient.id} value={patient.id}>
                              {patient.firstname} {patient.lastname || ""}{" "}
                              {patient.phone ? `(${patient.phone})` : ""}
                            </MenuItem>
                          ))}
                      </Select>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <Typography variant="subtitle2" gutterBottom>
                      {t("pharmacy.paymentStatus")}
                    </Typography>
                    <Select
                      value={paymentStatus}
                      onChange={(e) => setPaymentStatus(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">{t("pharmacy.allStatuses")}</MenuItem>
                      <MenuItem value="1">{t("pharmacy.unpaid")}</MenuItem>
                      <MenuItem value="2">{t("pharmacy.paid")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("pharmacy.actions")}
                    </Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        setFilterType("all");
                        setStartDate("");
                        setEndDate("");
                        setSelectedPatient("");
                        setPaymentStatus("");
                      }}
                      sx={{ height: "40px" }}
                    >
                      {t("pharmacy.resetFilters")}
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("pharmacy.startDate")}
                  </Typography>
                  <TextField
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("pharmacy.endDate")}
                  </Typography>
                  <TextField
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          )}
        </Grid>

        {/* Existing Medicine Orders */}
        {prescriptions.length > 0 ? (
          prescriptions.map((order) => {
            const isPaid = order.payment_status === 2;
            const isLoan = order.is_loan;

            return (
              <Grid item xs={12} md={6} key={order.id}>
                <Paper
                  elevation={isLoan ? 4 : isPaid ? 2 : 4}
                  sx={{
                    mb: 3,
                    padding: "24px",
                    borderRadius: "12px",
                    width: "100%",
                    boxShadow: isLoan
                      ? "0px 8px 24px rgba(0, 123, 255, 0.3)"
                      : isPaid
                      ? "0px 4px 12px rgba(0, 0, 0, 0.1)"
                      : "0px 8px 24px rgba(255, 0, 0, 0.3)",
                    backgroundColor: isLoan
                      ? "#e3f2fd"
                      : isPaid
                      ? "#f9f9f9"
                      : "#ffebee",
                    borderLeft: isLoan
                      ? "4px solid #1976d2"
                      : isPaid
                      ? "none"
                      : "4px solid #d32f2f",
                  }}
                >
                  {/* Header: Order Info and Actions */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="primary"
                      sx={{ fontSize: isSmallScreen ? "1rem" : "1.25rem" }}
                    >
                      {t("pharmacy.order")} #{order.id} - {order.date}
                    </Typography>

                    <Tooltip title={t("pharmacy.printReceipt")}>
                      <IconButton onClick={() => handleDeleteOrder(order.id)}>
                        <DeleteIcon color="warning" />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setSelectedOrderForEdit(order);
                          setEditDialogOpen(true);
                        }}
                      >
                        <EditIcon color="primary" />
                      </IconButton>
                      <IconButton onClick={() => handlePrintClick(order)}>
                        <PrintIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="primary"
                      sx={{ fontSize: isSmallScreen ? "1rem" : "1.25rem" }}
                    >
                      {t("pharmacy.section", "Section")}: {order.type || "N/A"}
                    </Typography>

                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="primary"
                      sx={{ fontSize: isSmallScreen ? "1rem" : "1.25rem" }}
                    >
                      {t("pharmacy.operation_room", "Operation Room")}:{" "}
                      {order.operation_room || "N/A"}
                    </Typography>
                  </Box>
                  {/* Patient Info */}
                  {order.patient && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mt: 2,
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                      }}
                    >
                      <Avatar sx={{ bgcolor: "primary.main" }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography fontWeight="bold">
                          {order.patient_details.firstname}{" "}
                          {order.patient_details.lastname || ""}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {order.patient_details.phone && (
                            <>
                              {t("reception.phone")}:{" "}
                              {order.patient_details.phone}
                              <br />
                            </>
                          )}
                          {order.patient_details.age && (
                            <>
                              {t("reception.age")}: {order.patient_details.age}
                            </>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Total Price */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    sx={{ mt: 2 }}
                  >
                    <Typography
                      fontSize={isSmallScreen ? "0.85rem" : "1rem"}
                      color={
                        isLoan
                          ? "info.main"
                          : !isPaid
                          ? "error.main"
                          : "text.primary"
                      }
                    >
                      <strong>{t("pharmacy.total_price")}:</strong>{" "}
                      {order.total_price} ؋
                    </Typography>
                  </Stack>

                  {/* Delete Order Button */}
                  {!isPaid && (
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      spacing={2}
                      sx={{ mt: 2 }}
                    >
                      <Tooltip title={t("pharmacy.deleteOrder")}>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteOrder(order.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                  <Divider sx={{ mt: 2, mb: 2 }} />

                  {/* Accordion: Medicine Order Details */}
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel-${order.id}-content`}
                      id={`panel-${order.id}-header`}
                    >
                      <Typography
                        fontWeight="bold"
                        color={!isPaid ? "error.main" : "text.primary"}
                      >
                        {t("pharmacy.medicineOrder")}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {order.items && order.items.length > 0 ? (
                        <Box sx={{ overflowX: "auto" }}>
                          <Table sx={{ minWidth: 650 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell align="center">#</TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.category")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.medicine")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.quantity")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.pricePerQuantity")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.subtotal")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.action")}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                              {order.items.map((item, index) => (
                                <TableRow
                                  key={item.id}
                                  sx={{
                                    "&:hover": { backgroundColor: "#f0f4f8" },
                                  }}
                                >
                                  <TableCell align="center">
                                    <Typography fontWeight="bold">
                                      {index + 1}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight="bold">
                                      {item.stock_medicine_category_display}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight="bold">
                                      {item.stock_medicine_display}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>{item.quantity}</Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>
                                      {item.sell_price_display}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>
                                      {item.sell_price_display * item.quantity}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    {!isPaid && (
                                      <>
                                        <Tooltip title={t("pharmacy.editItem")}>
                                          <IconButton
                                            color="primary"
                                            onClick={() => handleOpen(item)}
                                          >
                                            <EditIcon />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                          title={t("pharmacy.deleteItem")}
                                        >
                                          <IconButton
                                            color="error"
                                            onClick={() =>
                                              handleDeleteOrderItem(item.id)
                                            }
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : (
                        <Typography>{t("pharmacy.noMedicineOrder")}</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              </Grid>
            );
          })
        ) : (
          <Grid item xs={12}>
            <Box textAlign="center" mt={5}>
              <Typography variant="h6">
                {t("pharmacy.noMedicineOrder")}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Pagination Controls */}
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={10}
            mb={5}
          >
            <Box display="flex" alignItems="center">
              <Typography>{t("pharmacy.rowsPerPage")}: </Typography>
              <FormControl
                sx={{
                  ml: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
                size="small"
              >
                <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                  <MenuItem value={1000}>1000</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={Math.ceil(totalPrescriptions / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              color="primary"
            />
          </Box>
        </Grid>

        {/* Dialogs and Hidden Print Sections */}
        <ConfirmDialog />
        <ConfirmDialogItem />

        {editDialogOpen && (
          <FreeMedicineEdit
            open={editDialogOpen}
            orderData={selectedOrderForEdit}
            onClose={() => setEditDialogOpen(false)}
            token={token}
          />
        )}

        {/* Print Section (Hidden) */}
        {printData && (
          <div ref={printRef} style={{ display: "none" }}>
            <FreeMedicinePrint data={printData} />
          </div>
        )}
      </Grid>
    );
  }
};

export default FreeMedicineList;
